import { dropForeignCache, fetchMe, isTransportFailure, sessionKeys, type Me } from '@church/data'
import type { QueryClient } from '@tanstack/react-query'
import { api } from './api.ts'

/**
 * Session for route guards. `beforeLoad` runs outside React, so this reads
 * the same query-cache entry `useMe()` serves, via `fetchQuery` — guard and
 * hooks can never disagree about who is signed in.
 */
/**
 * Whether this page load has asked the server who is signed in yet. Until it
 * has, a cached `me` is only what the persisted cache *restored* — possibly
 * another person's — so the first guard of every boot goes to the network.
 */
let confirmedThisBoot = false

export async function ensureMe(queryClient: QueryClient): Promise<Me | null> {
  const restored = queryClient.getQueryData<Me | null>(sessionKeys.me())
  if (confirmedThisBoot && restored) {
    // Asked already this visit: the guard answers from that, and a stale copy
    // is refreshed behind the navigation rather than holding it up. A tab or a
    // page never waits on the network to learn who is signed in; a session
    // that ended shows up (as null) on the next guard.
    void queryClient.prefetchQuery({
      queryKey: sessionKeys.me(),
      queryFn: () => fetchMe(api),
      staleTime: 60_000,
      retry: false,
    })
    return restored
  }
  try {
    const me = await queryClient.fetchQuery({
      queryKey: sessionKeys.me(),
      queryFn: () => fetchMe(api),
      staleTime: confirmedThisBoot ? 60_000 : 0,
      retry: false,
    })
    if (!confirmedThisBoot) {
      confirmedThisBoot = true
      dropForeignCache(queryClient, restored, me)
    }
    return me
  } catch (error) {
    /**
     * The network is gone. Fall back to whatever the persisted cache holds.
     *
     * This is the check-in kiosk's boot path: an iPad that reloads mid-service
     * has no network to ask who is signed in, and throwing here puts the whole
     * app into an error boundary with a queue full of children behind it. A
     * cached session is not a security decision — the server re-checks every
     * request, and every one of them will fail while offline anyway — it is
     * only what lets the shell render the screen the volunteer is looking at.
     *
     * A *401* is different and must not be swallowed: that is the server
     * saying "not signed in", and falling back would leave somebody looking at
     * a stale identity. Only a transport failure gets the cache.
     */
    //
    // Eden reports a failed fetch as a 503 with the TypeError inside, so the
    // test is "did it reach the server", not "is there a status".
    if (!isTransportFailure(error)) throw error
    return queryClient.getQueryData<Me | null>(sessionKeys.me()) ?? null
  }
}
